import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, LogOut, Users, Package, CheckCircle, AlertCircle, Edit, Key, Home } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const AdminPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("transactions");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const fetchData = async () => {
    try {
      const [statsRes, transactionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setTransactions(transactionsRes.data);
      setUsers(usersRes.data?.users || usersRes.data || []);
    } catch (error) {
      toast.error(t('notifications.failedToLoad'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/super-admin/users/${userId}`, { role: newRole });
      toast.success(t('superAdmin.roleUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('notifications.error'));
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    try {
      // Use appropriate endpoint based on role
      const endpoint = user.role === "super_admin" 
        ? `${API}/super-admin/users/${selectedUserId}/reset-password`
        : `${API}/admin/users/${selectedUserId}/reset-password`;
        
      await axios.post(endpoint, {
        new_password: newPassword
      });
      toast.success("Mot de passe réinitialisé avec succès!");
      setShowPasswordDialog(false);
      setNewPassword("");
      setSelectedUserId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de la réinitialisation");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      created: "bg-brand-100 text-blue-800",
      tickets_uploaded: "bg-yellow-100 text-yellow-800",
      ocr_verified: "bg-green-100 text-green-800",
      admin_verified: "bg-green-100 text-green-800",
      completed: "bg-emerald-100 text-emerald-800",
      disputed: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity" 
              onClick={() => navigate('/')}
            >
              <img src="/logo-dark-cropped.png" alt="Middleman" className="h-5 md:h-6 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <Home className="h-4 w-4" />
                <span>Retour au site</span>
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <Badge variant="secondary" className="text-xs">{t('roles.admin')}</Badge>
              </div>
              {user.role === 'super_admin' && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings')}
                  className="flex items-center space-x-2"
                >
                  <Shield className="h-4 w-4" />
                  <span>Paramètres</span>
                </Button>
              )}
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="admin-logout-button"
                className="flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Utilisateurs totaux</CardTitle>
                  <Users className="h-4 w-4 text-brand-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stats?.total_users || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Transferts totaux</CardTitle>
                  <Package className="h-4 w-4 text-brand-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stats?.total_transactions || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">En attente de vérification</CardTitle>
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">{stats?.pending_verification || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Vérifiés</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{stats?.verified_transactions || 0}</div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for Transactions and Users */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="transactions">
                  <Package className="h-4 w-4 mr-2" />
                  {t('admin.allTransactions')} ({transactions.length})
                </TabsTrigger>
                <TabsTrigger value="users">
                  <Users className="h-4 w-4 mr-2" />
                  {t('admin.totalUsers')} ({users.length})
                </TabsTrigger>
              </TabsList>

              {/* Transactions Tab */}
              <TabsContent value="transactions">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('admin.allTransactions')}</CardTitle>
                    <CardDescription>{t('admin.manageVerify')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {transactions.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-transactions-admin">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">{t('admin.noTransactionsAdmin')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="admin-transactions-list">
                        {transactions.map((transaction) => (
                          <div
                            key={transaction.id}
                            data-testid={`admin-transaction-${transaction.id}`}
                            onClick={() => navigate(`/transactions/${transaction.id}`)}
                            className="p-4 border border-gray-200 rounded-lg hover:shadow-md cursor-pointer bg-white"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <h3 className="font-semibold text-gray-900">{transaction.event_name}</h3>
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                                    {formatStatus(transaction.status)}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm text-gray-600">
                                  <div>
                                    <span className="font-medium">{t('roles.buyer')}:</span> {transaction.buyer_email}
                                  </div>
                                  <div>
                                    <span className="font-medium">{t('roles.seller')}:</span> {transaction.seller_email}
                                  </div>
                                  <div>
                                    <span className="font-medium">{t('common.date')}:</span> {transaction.event_date}
                                  </div>
                                  <div>
                                    <span className="font-medium">{t('common.price')}:</span> ${transaction.price}
                                  </div>
                                  <div>
                                    <span className="font-medium">Plateforme:</span> {transaction.ticket_platform === 'Autre' ? transaction.custom_platform || 'Autre' : transaction.ticket_platform || 'N/A'}
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/transactions/${transaction.id}`);
                                }}
                                data-testid={`view-transaction-${transaction.id}`}
                              >
                                {t('common.viewDetails')}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('admin.totalUsers')}</CardTitle>
                    <CardDescription>View all registered users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {users.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">No users found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-4 font-medium text-gray-700">{t('common.name')}</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">{t('common.email')}</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">{t('common.role')}</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">{t('common.date')}</th>
                              {(user.role === "super_admin" || user.role === "admin") && (
                                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((u) => (
                              <tr key={u.id} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-4">{u.name}</td>
                                <td className="py-3 px-4">{u.email}</td>
                                <td className="py-3 px-4">
                                  {user.role === "super_admin" ? (
                                    <select
                                      className="px-2 py-1 border rounded text-sm"
                                      value={u.role}
                                      onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                                    >
                                      <option value="buyer">{t('roles.buyer')}</option>
                                      <option value="seller">{t('roles.seller')}</option>
                                      <option value="admin">{t('roles.admin')}</option>
                                      <option value="owner">{t('roles.owner')}</option>
                                      <option value="super_admin">{t('roles.super_admin')}</option>
                                    </select>
                                  ) : (
                                    <Badge variant="secondary">{t(`roles.${u.role}`)}</Badge>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-600">
                                  {new Date(u.created_at).toLocaleDateString()}
                                </td>
                                {(user.role === "super_admin" || user.role === "admin") && (
                                  <td className="py-3 px-4">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedUserId(u.id);
                                        setShowPasswordDialog(true);
                                      }}
                                      className="flex items-center space-x-1"
                                    >
                                      <Key className="h-4 w-4" />
                                      <span>Réinitialiser</span>
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Password Reset Dialog */}
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('superAdmin.resetPassword')}</DialogTitle>
                  <DialogDescription>
                    Enter a new password for this user (minimum 6 characters)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">{t('superAdmin.newPassword')}</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordDialog(false);
                      setNewPassword("");
                      setSelectedUserId(null);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleResetPassword}>
                    {t('superAdmin.resetPassword')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;